import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
} from "@mui/joy";
import {
  useUserMeQuery,
  useUserSendVerificationEmailMutation,
} from "../../__generated__/types-and-hooks";
import { Outlet, useNavigate } from "react-router-dom";
import { useTranslation } from "../../i18n";
import { useCallback } from "react";
import WarningIcon from "@mui/icons-material/Warning";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import { Link as RouterLink } from "react-router-dom";
import Link from "@mui/joy/Link";
import PersonIcon from "@mui/icons-material/Person";
import HomeIcon from "@mui/icons-material/Home";
import OnlineUsersToggle from "../../components/OnlineUsersToggle";
// import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import TeamCard from "./TeamCard";
import TeamActionCard from "./TeamActionCard";

export default function Dashboard() {
  const navigate = useNavigate();
  const t = useTranslation();
  const [userSendVerificationEmailMutation, userSendVerificationEmailRes] =
    useUserSendVerificationEmailMutation({});

  const userMeQuery = useUserMeQuery();
  const user = userMeQuery.data?.user_me;

  const handleUpdateProfile = useCallback(() => {
    navigate("/user/" + user?.slug);
  }, [navigate, user]);

  return (
    <>
      <Stack direction="row">
        <Breadcrumbs
          aria-label="breadcrumbs"
          sx={{
            "--Breadcrumbs-gap": "4px",
            m: 1,
          }}
          size="sm"
        >
          <Link component={RouterLink} color="neutral" to="/">
            <HomeIcon />
          </Link>
          <Typography level="title-sm" />
        </Breadcrumbs>
        <OnlineUsersToggle />
      </Stack>

      <Box sx={{ p: { sm: 2, xs: 0 }, maxWidth: 900, margin: "0 auto" }}>
        {/* Verify Email */}
        {user && !user?.verifiedEmail && !userSendVerificationEmailRes.data && (
          <Alert
            sx={{
              m: 2,
              mb: 0,
              backgroundColor:
                "color-mix(in srgb, var(--joy-palette-warning-outlinedBorder), transparent 30%)",
            }}
            startDecorator={<WarningIcon fontSize="medium" />}
            variant="outlined"
            color="warning"
            endDecorator={
              <Button
                disabled={userSendVerificationEmailRes.loading}
                color="success"
                sx={{ mr: 1 }}
                startDecorator={<EmailOutlinedIcon />}
                onClick={() => userSendVerificationEmailMutation()}
              >
                {t("Send the link")}
              </Button>
            }
          >
            {t("Please verify your email by opening a verification link.")}
          </Alert>
        )}
        {user && !user?.picture && (
          <Alert
            sx={{
              m: 2,
              mb: 0,
              backgroundColor:
                "color-mix(in srgb, var(--joy-palette-warning-outlinedBorder), transparent 30%)",
            }}
            variant="outlined"
            color="warning"
            startDecorator={<PersonIcon fontSize="medium" />}
          >
            <CardContent>
              {t(
                "Add your profile photo to help brands & agencies recognize you."
              )}
            </CardContent>
            <Button variant="solid" onClick={handleUpdateProfile}>
              {t("Update Profile")}
            </Button>
          </Alert>
        )}

        <Box
          sx={{
            p: 2,
            pb: 0,
            width: "100%",
            display: "grid",
            gridTemplateColumns: {
              sm: "repeat(auto-fill, minmax(min(100%, 200px), 1fr))",
              xs: "repeat(auto-fill, minmax(min(100%, 1fr), 1fr))",
            },
          }}
          gap={2}
        >
          {user?.teamEdges.map((teamEdge) => (
            <TeamCard
              slug={teamEdge.team.slug}
              key={teamEdge.team.id}
              teamId={teamEdge.team.id}
              name={teamEdge.team.name}
              picture={teamEdge.team.picture}
              description={teamEdge.team.description}
              kind={teamEdge.team.kind}
            />
          ))}

          <TeamActionCard />
          <Card
            variant="outlined"
            orientation="horizontal"
            sx={{
              background: "hsl(0deg 0% 31.06% / 35%)",
              "&:hover": {
                boxShadow: "md",
                borderColor: "neutral.outlinedHoverBorder",
                background: "hsl(0deg 0% 51.06% / 35%)",
              },
            }}
          >
            <LanguageOutlinedIcon fontSize="large" />
            <CardContent>
              <Typography level="title-lg" id="card-description">
                {t("Network")}
              </Typography>
              <Typography
                level="body-sm"
                aria-describedby="card-description"
                sx={{ mb: 1 }}
              >
                <Link
                  component={RouterLink}
                  color="neutral"
                  to="./network"
                  overlay
                  underline="none"
                  sx={{ color: "text.tertiary" }}
                >
                  {t("Explore brands and agencies on QCSKU.")}
                </Link>
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>
      <Outlet />
    </>
  );
}
